import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDown, XCircle, XIcon } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const multiSelectVariants = cva('m-1', {
  variants: {
    variant: {
      default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
      secondary:
        'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
      destructive:
        'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
      inverted: 'inverted',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface MultiSelectOption {
  id: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  options: MultiSelectOption[];
  /** Controlled value */
  value: string[];
  /** Change handler */
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  maxCount?: number;
  modalPopover?: boolean;
  loading?: boolean;
  className?: string;
  error?: boolean | string;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      value = [],
      onValueChange,
      variant,
      placeholder = 'Select options',
      maxCount = 3,
      modalPopover = false,
      className,
      loading,
      error,
      ...props
    },
    ref
  ) => {
    const parentRef = React.useRef<HTMLButtonElement>(null);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    const toggleOption = (optionValue: string) => {
      const newValues = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onValueChange(newValues);
    };

    const handleClear = () => onValueChange([]);

    const clearExtraOptions = () => {
      const newValues = value.slice(0, maxCount);
      onValueChange(newValues);
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            {...props}
            ref={parentRef}
            onClick={() => setIsPopoverOpen((prev) => !prev)}
            className={cn(
              'flex w-full p-1 rounded-md border min-h-[44px] h-auto items-center justify-between bg-inherit normal-case hover:bg-inherit [&_svg]:pointer-events-auto',
              className,
              // Error border
              error
                ? 'border-destructive focus-visible:ring-500'
                : 'border-input'
            )}
          >
            {value.length > 0 ? (
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-wrap items-center">
                  {value.slice(0, maxCount).map((val) => {
                    const option = options.find((o) => o.value === val);
                    if (!option) return null;
                    const Icon = option.icon;
                    return (
                      <Badge
                        key={val}
                        className={cn('h-7', multiSelectVariants({ variant }))}
                      >
                        {Icon && <Icon className="h-2 w-2 mr-2" />}
                        {option.id}
                        <XCircle
                          className="ml-2 h-2 w-2 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOption(val);
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {value.length > maxCount && (
                    <Badge
                      className={cn(
                        'bg-transparent text-foreground border-foreground/10 hover:bg-transparent',
                        multiSelectVariants({ variant })
                      )}
                    >
                      {`+ ${value.length - maxCount} more`}
                      <XCircle
                        className="ml-1 h-2 w-2 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex items-center">
                  <XIcon
                    className="h-4 mx-2 cursor-pointer text-muted-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="h-full min-h-6"
                  />
                  <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm text-muted-foreground mx-3 font-normal">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
              </div>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0"
          align="start"
          style={{ width: parentRef.current?.offsetWidth }}
        >
          <Command>
            <CommandList>
              <CommandEmpty>
                {loading ? 'Loading...' : 'No options'}
              </CommandEmpty>
              <CommandGroup>
                {options?.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'text-primary'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      {option.icon && (
                        <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{option.id}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
